
import React, { useState, useEffect, useRef } from 'react';
import { DataProviderInstance } from '../../../api/DataProvider';
import styles from './Whatsapp.module.css';
import Loading from '../../../components/loading/Loading';
import InputField from '../../../components/inputfield/InputField';
import CustomButton from '../../../components/buttons/CustomButton';
import AreaTitle from '../../../components/areaTitle/AreaTitle';
import ExternalIntegrationInfo from '../../../components/external-integration-info/ExternalIntegrationInfo';
import SenderOriginCard from './SenderOriginCard';
import OriginOverviewCard from './OriginOverviewCard';
import { QRCodeSVG } from 'qrcode.react';
import { useWhatsAppSender } from '../../../contexts/WhatsAppSenderContext';
import {
  Typography,
  Switch,
  FormControlLabel,
  Paper,
  Alert,
  Chip,
  Divider,
  Box,
  CircularProgress,
  Stepper,
  Step,
  StepButton,
  StepContent,
  TextField
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import RefreshIcon from '@mui/icons-material/Refresh';
import SyncIcon from '@mui/icons-material/Sync';
import QrCodeIcon from '@mui/icons-material/QrCode2';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ColorPalette from '../../../colorpalette/ColorPalette';
import useSubscriptionFeatures from '../../../hooks/useSubscriptionFeatures';
import { SUBSCRIPTION_PERMISSIONS } from '../../../consts/SubscriptionFeatures';
import FeatureLockBanner from '../../../components/subscription/FeatureLockBanner';
import PlanUpgradeModal from '../../../components/subscription/PlanUpgradeModal';

// Constants for timer values - easy to modify
const STATUS_CHECK_INTERVAL = 5000; // 5 seconds
const QR_CODE_EXPIRATION_TIME = 20000; // 20 seconds

const ZAPI_SITE_URL = 'https://www.z-api.io';

// Rótulos pt-BR do status salvo da instância própria (last_status)
const LAST_STATUS_LABELS = {
  not_configured: 'Não configurado',
  awaiting_connection: 'Aguardando conexão',
  connected: 'Conectado',
  disconnected: 'Desconectado',
  error: 'Erro'
};

const cardSx = {
  backgroundColor: ColorPalette.white,
  borderRadius: '12px',
  padding: '24px',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.03)',
  border: `1px solid ${ColorPalette.border}`
};

/**
 * Tela de configuração do WhatsApp da clínica, organizada em dois cards (2026-09-18):
 *
 *   1. "Por onde suas mensagens saem"  → SenderOriginCard: uma frase de status derivada de
 *      get_sender_status.php e no máximo dois botões.
 *   2. "Usar meu próprio número"       → stepper vertical de 3 passos: credenciais Z-API,
 *      conectar o WhatsApp (QR Code / código no telefone) e ativar.
 *
 * Regra importante: QR Code, código no telefone e device info NUNCA são chamados antes de
 * existirem credenciais salvas — era daí que vinha o erro vermelho automático ao abrir a tela.
 */
function Whatsapp() {
  const dataProvider = DataProviderInstance();
  const {
    loading: senderLoading,
    state: senderState,
    own: senderOwn,
    platform: senderPlatform,
    refresh: refreshSenderStatus
  } = useWhatsAppSender();

  // Número próprio (Z-API) é liberado a partir do Profissional; no Essencial a
  // tela fica visível com o aviso de plano (mesmo padrão de FiscalSettings).
  const { hasPermission } = useSubscriptionFeatures();
  const zapiAllowed = hasPermission(SUBSCRIPTION_PERMISSIONS.ZAPI_INTEGRATION);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);

  // Config / carregamento
  const [loading, setLoading] = useState(true);
  const [configData, setConfigData] = useState(null);

  // Passo 1 — credenciais
  const [instanceId, setInstanceId] = useState('');
  const [instanceToken, setInstanceToken] = useState('');
  // Client-Token da conta Z-API da clínica (cada clínica contrata a própria conta)
  const [securityToken, setSecurityToken] = useState('');
  const [saveMessages, setSaveMessages] = useState(true);
  const [formErrors, setFormErrors] = useState({});
  const [savingCredentials, setSavingCredentials] = useState(false);

  // Passo 2 — conexão
  const [testing, setTesting] = useState(false);
  const [instanceStatus, setInstanceStatus] = useState(null);
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [lastChecked, setLastChecked] = useState(null);
  const [connectionMethod, setConnectionMethod] = useState('qrcode');
  const [qrCode, setQrCode] = useState(null);
  const [loadingQrCode, setLoadingQrCode] = useState(false);
  const [qrCodeError, setQrCodeError] = useState(null);
  const [qrCodeExpired, setQrCodeExpired] = useState(false);
  const [qrCodeTimeRemaining, setQrCodeTimeRemaining] = useState(QR_CODE_EXPIRATION_TIME / 1000);
  const [phoneForCode, setPhoneForCode] = useState('');
  const [phoneCode, setPhoneCode] = useState(null);
  const [loadingPhoneCode, setLoadingPhoneCode] = useState(false);
  const [phoneCodeError, setPhoneCodeError] = useState(null);

  // Passo 3 — ativação
  const [savingUseOwnNumber, setSavingUseOwnNumber] = useState(false);

  // Navegação do card de passos. `null` = segue o passo calculado pelo progresso real.
  const [manualStep, setManualStep] = useState(null);
  const [manualExpanded, setManualExpanded] = useState(null);

  // Mensagem única de feedback das ações da tela ({ severity, text })
  const [actionMessage, setActionMessage] = useState(null);

  const setupSectionRef = useRef(null);
  const statusCheckIntervalRef = useRef(null);
  const qrCodeExpirationTimerRef = useRef(null);

  const clearTimers = () => {
    if (statusCheckIntervalRef.current) {
      clearInterval(statusCheckIntervalRef.current);
      statusCheckIntervalRef.current = null;
    }
    if (qrCodeExpirationTimerRef.current) {
      clearInterval(qrCodeExpirationTimerRef.current);
      qrCodeExpirationTimerRef.current = null;
    }
  };

  // ── Carregamento da config ────────────────────────────────────────────────
  const rowHasCredentials = (config) => !!(
    config
    && config.instance_id
    && String(config.instance_id).trim() !== ''
    && config.has_instance_token
  );

  // options.background = não mostra o loading de página inteira
  // options.syncForm   = repõe os campos do formulário a partir da config salva
  const fetchWhatsAppConfig = (options = {}) => {
    const { background = false, syncForm = false, initial = false } = options;

    if (!background) {
      setLoading(true);
    }

    dataProvider.GetWhatsappConfig((response) => {
      if (!background) {
        setLoading(false);
      }

      const config = (response && response.success) ? (response.config || null) : null;
      setConfigData(config);

      if (syncForm || initial) {
        setInstanceId(config && config.instance_id ? config.instance_id : '');
        setInstanceToken('');
        setFormErrors({});
        if (config) {
          setSaveMessages(config.save_messages === 1 || config.save_messages === true);
        }
      }

      // Só consulta a Z-API quando já existem credenciais salvas: sem isso a tela
      // abria disparando chamadas que só podiam falhar.
      if (initial && rowHasCredentials(config)) {
        runStatusCheck({ silent: true });
      }
    });
  };

  useEffect(() => {
    fetchWhatsAppConfig({ initial: true });
    return clearTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Passo 2: consultar o status da instância própria ──────────────────────
  // silent = checagem de fundo (polling / abertura da tela): não mostra mensagem
  const runStatusCheck = (options = {}) => {
    const { silent = false } = options;

    if (!silent) {
      setTesting(true);
      setActionMessage(null);
    }

    dataProvider.TestWhatsappConfig((response) => {
      setTesting(false);
      setLastChecked(new Date());

      const status = (response && response.status) ? response.status : null;
      setInstanceStatus(status);

      const connected = !!(status && status.connected && status.phone_connected);

      if (connected) {
        clearTimers();
        setQrCode(null);
        setQrCodeError(null);
        setPhoneCode(null);
        setPhoneCodeError(null);
        dataProvider.GetWhatsappDeviceInfo((deviceResponse) => {
          if (deviceResponse && deviceResponse.success && deviceResponse.device) {
            setDeviceInfo(deviceResponse.device);
          }
        });
        setManualStep(null);
      } else {
        setDeviceInfo(null);
      }

      if (connected) {
        setActionMessage({ severity: 'success', text: 'Seu número está conectado.' });
      } else if (!silent) {
        setActionMessage({
          severity: 'warning',
          text: 'Seu número ainda não está conectado. Use o QR Code ou o código no telefone abaixo para parear o WhatsApp.'
        });
      }

      // test_company_whatsapp_config.php grava o resultado em last_status, então
      // recarregamos o status para o passo 3 destravar na hora.
      if (connected || !silent) {
        refreshSenderStatus();
        fetchWhatsAppConfig({ background: true });
      }
    });
  };

  // ── Passo 2: QR Code ──────────────────────────────────────────────────────
  const fetchQRCode = () => {
    if (loadingQrCode) return;

    setLoadingQrCode(true);
    setQrCodeError(null);
    setQrCodeExpired(false);
    setQrCodeTimeRemaining(QR_CODE_EXPIRATION_TIME / 1000);
    clearTimers();

    dataProvider.GetWhatsappQRCode((response) => {
      setLoadingQrCode(false);

      if (response && response.success && response.qrcode) {
        setQrCode(response.qrcode);

        qrCodeExpirationTimerRef.current = setInterval(() => {
          setQrCodeTimeRemaining((prevTime) => {
            if (prevTime <= 1) {
              clearTimers();
              setQrCodeExpired(true);
              return 0;
            }
            return prevTime - 1;
          });
        }, 1000);

        statusCheckIntervalRef.current = setInterval(() => {
          runStatusCheck({ silent: true });
        }, STATUS_CHECK_INTERVAL);
      } else {
        setQrCodeError((response && response.error) || 'Não foi possível gerar o QR Code. Confira suas credenciais e tente de novo.');
        setQrCode(null);
      }
    });
  };

  // ── Passo 2: código no telefone ───────────────────────────────────────────
  const fetchPhoneCode = () => {
    if (loadingPhoneCode || !phoneForCode) return;

    setLoadingPhoneCode(true);
    setPhoneCodeError(null);

    dataProvider.GetWhatsappPhoneCode(phoneForCode, (response) => {
      setLoadingPhoneCode(false);

      if (response && response.success && response.phonecode) {
        setPhoneCode(response.phonecode);

        clearTimers();
        statusCheckIntervalRef.current = setInterval(() => {
          runStatusCheck({ silent: true });
        }, STATUS_CHECK_INTERVAL);
      } else {
        setPhoneCodeError((response && response.error) || 'Não foi possível gerar o código. Confira suas credenciais e tente de novo.');
      }
    });
  };

  // ── Passo 1: salvar credenciais ───────────────────────────────────────────
  const validateCredentials = () => {
    const errors = {};

    if (!instanceId || !instanceId.trim()) {
      errors.instanceId = 'Informe o ID da instância';
    }

    // Só exige o token quando ainda não há token salvo: numa config existente o
    // campo pode ficar vazio (o backend mantém o token já salvo).
    if (!configData?.has_instance_token && (!instanceToken || !instanceToken.trim())) {
      errors.instanceToken = 'Informe o token da instância';
    }

    if (!configData?.has_security_token && (!securityToken || !securityToken.trim())) {
      errors.securityToken = 'Informe o token de segurança da conta';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveCredentials = () => {
    if (savingCredentials) return;

    if (!validateCredentials()) return;

    setSavingCredentials(true);
    setActionMessage(null);

    // is_active sempre 1: a tela não expõe mais esse toggle — quem liga/desliga o
    // número próprio é o passo 3 (use_own_number).
    dataProvider.SetWhatsappConfig(
      instanceId.trim(),
      instanceToken.trim(),
      securityToken.trim(),
      true,
      saveMessages,
      (response) => {
        setSavingCredentials(false);

        if (response && response.success) {
          setInstanceToken('');
          setSecurityToken('');
          setInstanceStatus(null);
          setDeviceInfo(null);
          setManualStep(null);
          setActionMessage({ severity: 'success', text: 'Credenciais salvas. Agora conecte seu WhatsApp no passo 2.' });
          refreshSenderStatus();
          fetchWhatsAppConfig({ background: true, syncForm: true });
          // Teste imediato: se a instância já estiver pareada, o passo 2 conclui sozinho.
          runStatusCheck({ silent: true });
        } else {
          setActionMessage({
            severity: 'error',
            text: (response && response.error) || 'Não foi possível salvar as credenciais.'
          });
        }
      }
    );
  };

  // ── Passo 3: ligar/desligar o número próprio ──────────────────────────────
  const handleToggleUseOwnNumber = (value) => {
    if (savingUseOwnNumber) return;

    if (value && !step2Done) {
      setActionMessage({
        severity: 'warning',
        text: 'Conecte seu número no passo 2 antes de ativar.'
      });
      setManualExpanded(true);
      setManualStep(1);
      return;
    }

    setSavingUseOwnNumber(true);
    setActionMessage(null);

    dataProvider.SetWhatsappSenderToggles({ useOwnNumber: value }, (response) => {
      setSavingUseOwnNumber(false);

      if (response && response.success) {
        setManualStep(null);
        setActionMessage({
          severity: 'success',
          text: value
            ? 'Pronto! Suas mensagens agora saem pelo seu número.'
            : 'Seu número foi desligado. As mensagens voltam a sair pelo WhatsApp Web, com o texto pronto.'
        });
        refreshSenderStatus();
        fetchWhatsAppConfig({ background: true });
      } else if (response && response.code === 'OWN_NUMBER_NOT_CONNECTED') {
        // 409 do backend: a linha não está com last_status = connected.
        setActionMessage({
          severity: 'warning',
          text: 'Conecte seu número no passo 2 antes de ativar.'
        });
        setManualExpanded(true);
        setManualStep(1);
        refreshSenderStatus();
        fetchWhatsAppConfig({ background: true });
      } else {
        setActionMessage({
          severity: 'error',
          text: (response && response.error) || 'Não foi possível atualizar a configuração.'
        });
      }
    });
  };

  // ── Progresso dos passos ──────────────────────────────────────────────────
  const hasCredentials = rowHasCredentials(configData);
  const liveConnected = !!(instanceStatus && instanceStatus.connected && instanceStatus.phone_connected);
  const savedConnected = configData?.last_status === 'connected' || senderOwn?.status === 'connected';

  const step1Done = hasCredentials;
  // Quando já testamos nesta sessão, vale o resultado ao vivo; senão, o status salvo.
  const step2Done = step1Done && (instanceStatus ? liveConnected : savedConnected);
  const useOwnNumber = !!(senderOwn && senderOwn.use_own_number);
  const step3Done = step2Done && useOwnNumber;

  const computedStep = !step1Done ? 0 : (!step2Done ? 1 : 2);
  const activeStep = manualStep !== null ? manualStep : computedStep;

  // Número próprio ligado e conectado: o card de passos nasce fechado (resumo + "Gerenciar").
  const autoCollapsed = senderState === 'own_connected' && step2Done;
  const setupExpanded = manualExpanded !== null ? manualExpanded : !autoCollapsed;

  const devicePhone = deviceInfo?.phone || null;

  const scrollToSetup = () => {
    if (setupSectionRef.current) {
      setupSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleManageInstance = () => {
    setManualExpanded(true);
    setManualStep(null);
    scrollToSetup();
  };

  const handleReconnect = () => {
    setManualExpanded(true);
    setManualStep(1);
    scrollToSetup();
  };

  const handleStepClick = (index) => {
    if (index === 1 && !step1Done) return;
    if (index === 2 && !step2Done) return;
    setManualStep(index);
  };

  // ── Blocos de renderização do card 2 ──────────────────────────────────────
  const renderSavedStatusChip = () => {
    if (!configData || !configData.last_status) return null;

    const label = LAST_STATUS_LABELS[configData.last_status] || configData.last_status;
    const chipColor = configData.last_status === 'connected'
      ? 'success'
      : (configData.last_status === 'error' || configData.last_status === 'disconnected' ? 'error' : 'default');

    let checkedAtLabel = null;
    if (configData.last_status_checked_at) {
      const parsed = new Date(String(configData.last_status_checked_at).replace(' ', 'T'));
      checkedAtLabel = isNaN(parsed.getTime())
        ? configData.last_status_checked_at
        : parsed.toLocaleString('pt-BR');
    }

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Typography variant="body2" color="textSecondary">Status do seu número:</Typography>
        <Chip size="small" label={label} color={chipColor} variant="outlined" />
        {checkedAtLabel && (
          <Typography variant="caption" color="textSecondary">
            Verificado em {checkedAtLabel}
          </Typography>
        )}
        {configData.last_status_error && (
          <Typography variant="caption" color="error">
            {configData.last_status_error}
          </Typography>
        )}
      </Box>
    );
  };

  const renderStep1 = () => (
    <Box sx={{ pb: 2 }}>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        A conta de WhatsApp é contratada no site da Z-API, fora da Gabriela. Depois de criar a
        sua instância por lá, copie o ID e o token da instância e o token de segurança da sua
        conta (Client-Token, em Segurança) e cole aqui.
      </Typography>

      <Box sx={{ mb: 2 }}>
        <CustomButton
          variant="outlined"
          style="secondary"
          label="Acessar site da Z-API"
          onClick={() => window.open(ZAPI_SITE_URL, '_blank', 'noopener,noreferrer')}
          endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
        />
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <TextField
          size="small"
          fullWidth
          label="ID da instância"
          value={instanceId}
          onChange={(e) => setInstanceId(e.target.value)}
          disabled={savingCredentials}
          error={!!formErrors.instanceId}
          helperText={formErrors.instanceId || ' '}
          sx={{ flex: 1, minWidth: 240 }}
        />
        <TextField
          size="small"
          fullWidth
          type="password"
          label="Token da instância"
          value={instanceToken}
          onChange={(e) => setInstanceToken(e.target.value)}
          disabled={savingCredentials}
          error={!!formErrors.instanceToken}
          placeholder={configData?.has_instance_token ? 'Token salvo' : ''}
          helperText={
            formErrors.instanceToken
            || (configData?.has_instance_token ? 'Deixe em branco para manter o token salvo.' : ' ')
          }
          InputLabelProps={{ shrink: true }}
          sx={{ flex: 1, minWidth: 240 }}
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <TextField
          size="small"
          fullWidth
          type="password"
          label="Token de segurança da conta"
          value={securityToken}
          onChange={(e) => setSecurityToken(e.target.value)}
          disabled={savingCredentials}
          error={!!formErrors.securityToken}
          placeholder={configData?.has_security_token ? 'Token salvo' : ''}
          helperText={
            formErrors.securityToken
            || (configData?.has_security_token
              ? 'Deixe em branco para manter o token salvo.'
              : 'Client-Token da sua conta Z-API, o mesmo para todas as instâncias dela.')
          }
          InputLabelProps={{ shrink: true }}
          sx={{ maxWidth: 496 }}
        />
      </Box>

      <CustomButton
        variant="contained"
        style="secondary"
        label={savingCredentials ? 'Salvando...' : 'Salvar'}
        onClick={handleSaveCredentials}
        disabled={savingCredentials}
        icon={savingCredentials ? <Loading size={16} /> : <SaveIcon />}
      />
    </Box>
  );

  const renderDevicePanel = () => {
    if (!deviceInfo) return null;

    return (
      <Paper
        elevation={0}
        sx={{
          backgroundColor: ColorPalette.blueLight5,
          borderRadius: '12px',
          padding: '16px',
          border: `1px solid ${ColorPalette.border}`,
          mb: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <CheckCircleIcon sx={{ color: ColorPalette.success }} fontSize="small" />
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            WhatsApp conectado
          </Typography>
        </Box>
        <Box className={styles.deviceInfoContainer}>
          {deviceInfo.imgUrl && (
            <Box className={styles.profileImageContainer}>
              <Box
                component="img"
                src={deviceInfo.imgUrl}
                alt="Perfil"
                className={styles.profileImage}
              />
            </Box>
          )}
          <Box className={styles.deviceDetailsContainer}>
            {deviceInfo.name && (
              <Box className={styles.statusRow}>
                <Typography variant="body2" color="textSecondary">Conta:</Typography>
                <Typography variant="body2" fontWeight="medium">{deviceInfo.name}</Typography>
              </Box>
            )}
            {deviceInfo.phone && (
              <Box className={styles.statusRow}>
                <Typography variant="body2" color="textSecondary">Telefone:</Typography>
                <Typography variant="body2" fontWeight="medium">{deviceInfo.phone}</Typography>
              </Box>
            )}
            {deviceInfo.device && deviceInfo.device.device_model && (
              <Box className={styles.statusRow}>
                <Typography variant="body2" color="textSecondary">Aparelho:</Typography>
                <Typography variant="body2" fontWeight="medium">{deviceInfo.device.device_model}</Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Paper>
    );
  };

  const renderConnectionOptions = () => (
    <Paper
      elevation={0}
      sx={{
        backgroundColor: ColorPalette.white,
        borderRadius: '12px',
        padding: '16px',
        border: `1px solid ${ColorPalette.border}`,
        mb: 2
      }}
    >
      <Box sx={{ display: 'flex', borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Box
          onClick={() => setConnectionMethod('qrcode')}
          sx={{
            flex: 1,
            textAlign: 'center',
            py: 1.5,
            cursor: 'pointer',
            borderBottom: connectionMethod === 'qrcode' ? 2 : 0,
            borderColor: connectionMethod === 'qrcode' ? ColorPalette.secondary : 'transparent',
            color: connectionMethod === 'qrcode' ? ColorPalette.secondary : 'text.secondary',
            fontWeight: connectionMethod === 'qrcode' ? 'medium' : 'normal',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1
          }}
        >
          <QrCodeIcon fontSize="small" />
          <Typography variant="body2">Ler QR Code</Typography>
        </Box>
        <Box
          onClick={() => setConnectionMethod('phonecode')}
          sx={{
            flex: 1,
            textAlign: 'center',
            py: 1.5,
            cursor: 'pointer',
            borderBottom: connectionMethod === 'phonecode' ? 2 : 0,
            borderColor: connectionMethod === 'phonecode' ? ColorPalette.secondary : 'transparent',
            color: connectionMethod === 'phonecode' ? ColorPalette.secondary : 'text.secondary',
            fontWeight: connectionMethod === 'phonecode' ? 'medium' : 'normal',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1
          }}
        >
          <WhatsAppIcon fontSize="small" />
          <Typography variant="body2">Código no telefone</Typography>
        </Box>
      </Box>

      {connectionMethod === 'qrcode' ? (
        <Box>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            No seu celular, abra o WhatsApp em Aparelhos conectados e leia o QR Code abaixo.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <CustomButton
              variant="contained"
              style="secondary"
              label={qrCode ? 'Atualizar QR Code' : 'Gerar QR Code'}
              onClick={fetchQRCode}
              icon={<QrCodeIcon />}
              disabled={loadingQrCode}
            />
          </Box>

          {loadingQrCode && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          )}

          {!loadingQrCode && qrCodeError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {qrCodeError}
            </Alert>
          )}

          {!loadingQrCode && !qrCodeError && qrCode && qrCode.value && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 1 }}>
              <Box
                sx={{
                  border: '1px solid #eee',
                  borderRadius: '4px',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                  opacity: qrCodeExpired ? 0.3 : 1,
                  filter: qrCodeExpired ? 'grayscale(100%)' : 'none'
                }}
              >
                <QRCodeSVG value={qrCode.value} size={250} level="H" includeMargin={true} />
              </Box>

              {qrCodeExpired ? (
                <>
                  <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                    Este QR Code expirou.
                  </Typography>
                  <CustomButton
                    variant="contained"
                    style="secondary"
                    label="Gerar outro QR Code"
                    onClick={fetchQRCode}
                    icon={<RefreshIcon />}
                  />
                </>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CircularProgress
                    variant="determinate"
                    value={(qrCodeTimeRemaining / (QR_CODE_EXPIRATION_TIME / 1000)) * 100}
                    size={20}
                    sx={{ mr: 1, color: qrCodeTimeRemaining < 5 ? 'error.main' : 'primary.main' }}
                  />
                  <Typography
                    variant="caption"
                    color={qrCodeTimeRemaining < 5 ? 'error' : 'textSecondary'}
                  >
                    Este QR Code expira em {qrCodeTimeRemaining} segundos
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      ) : (
        <Box>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Informe o número do WhatsApp da clínica para receber um código de 8 dígitos.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 2 }}>
            <InputField
              Label="Número de telefone"
              Value={phoneForCode}
              OnUpdateValue={(value) => setPhoneForCode(value)}
              Mask="+99 (99) 99999-9999"
              Disabled={loadingPhoneCode}
            />
            <CustomButton
              variant="contained"
              style="secondary"
              label={phoneCode ? 'Gerar novo código' : 'Gerar código'}
              onClick={fetchPhoneCode}
              disabled={!phoneForCode || loadingPhoneCode}
            />
          </Box>

          {loadingPhoneCode && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          )}

          {!loadingPhoneCode && phoneCodeError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {phoneCodeError}
            </Alert>
          )}

          {!loadingPhoneCode && !phoneCodeError && phoneCode && phoneCode.code && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2 }}>
              <Box
                sx={{
                  border: '1px solid #eee',
                  borderRadius: '4px',
                  p: 3,
                  mb: 2,
                  backgroundColor: '#f9f9f9',
                  textAlign: 'center',
                  minWidth: '250px'
                }}
              >
                <Typography variant="h3" sx={{ fontWeight: 'bold', letterSpacing: '0.1em' }}>
                  {phoneCode.code}
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary" align="center">
                No WhatsApp do celular, em Aparelhos conectados, toque em "Conectar com número de
                telefone" e digite este código.
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );

  const renderStep2 = () => (
    <Box sx={{ pb: 2 }}>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Agora é preciso parear o WhatsApp da clínica com a sua instância. Se já tiver feito isso
        na Z-API, use o botão abaixo para conferir.
      </Typography>

      {renderSavedStatusChip()}

      <Box sx={{ mb: 2 }}>
        <CustomButton
          variant={step2Done ? 'outlined' : 'contained'}
          style="secondary"
          label={testing ? 'Testando...' : 'Testar conexão'}
          onClick={() => runStatusCheck()}
          disabled={testing}
          icon={testing ? <Loading size={16} /> : <SyncIcon />}
        />
        {lastChecked && (
          <Typography variant="caption" color="textSecondary" sx={{ ml: 2 }}>
            Última verificação: {lastChecked.toLocaleString('pt-BR')}
          </Typography>
        )}
      </Box>

      {!step2Done && renderConnectionOptions()}
      {step2Done && (deviceInfo ? renderDevicePanel() : (
        <Alert severity="success">Seu número está conectado.</Alert>
      ))}
    </Box>
  );

  const renderStep3 = () => (
    <Box sx={{ pb: 2 }}>
      <Box>
        <FormControlLabel
          control={
            <Switch
              checked={useOwnNumber}
              onChange={(e) => handleToggleUseOwnNumber(e.target.checked)}
              disabled={savingUseOwnNumber || (!useOwnNumber && !step2Done)}
              color="primary"
            />
          }
          label={
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              Usar meu próprio número
            </Typography>
          }
        />
        <Box sx={{ pl: 4, mt: -0.5 }}>
          <Typography variant="body2" color="textSecondary">
            Ao ativar, confirmações, avisos e lembretes passam a sair sozinhos pelo seu número, e
            chat, documentos e envio em massa ficam disponíveis. Desligar não apaga as credenciais.
          </Typography>
          {!step2Done && (
            <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
              Conecte seu número no passo 2 para poder ativar.
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );

  const renderSetupCollapsed = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CheckCircleIcon sx={{ color: ColorPalette.success }} fontSize="small" />
        <Typography variant="body2" color="textSecondary">
          Instância {configData?.instance_id || ''} conectada
          {devicePhone ? ` · telefone ${devicePhone}` : ''}
        </Typography>
      </Box>
      <CustomButton
        variant="outlined"
        style="secondary"
        label="Gerenciar"
        onClick={() => setManualExpanded(true)}
      />
    </Box>
  );

  const renderSetupCard = () => (
    <Paper ref={setupSectionRef} elevation={0} sx={cardSx}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <WhatsAppIcon sx={{ color: ColorPalette.secondary, mr: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Usar meu próprio número (opcional)
          </Typography>
        </Box>
        {setupExpanded && autoCollapsed && (
          <CustomButton
            variant="text"
            style="secondary"
            label="Fechar"
            onClick={() => setManualExpanded(false)}
          />
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      {!setupExpanded ? renderSetupCollapsed() : (
        <>
          {actionMessage && (
            <Alert
              severity={actionMessage.severity}
              sx={{ mb: 2 }}
              onClose={() => setActionMessage(null)}
            >
              {actionMessage.text}
            </Alert>
          )}

          <Stepper activeStep={activeStep} orientation="vertical" nonLinear>
            <Step completed={step1Done}>
              <StepButton onClick={() => handleStepClick(0)} optional={
                <Typography variant="caption" color="textSecondary">
                  {step1Done ? 'Credenciais salvas' : 'Informe o ID e o token da sua instância'}
                </Typography>
              }>
                Credenciais da Z-API
              </StepButton>
              <StepContent>{renderStep1()}</StepContent>
            </Step>

            <Step completed={step2Done} disabled={!step1Done}>
              <StepButton
                onClick={() => handleStepClick(1)}
                disabled={!step1Done}
                optional={
                  <Typography variant="caption" color={step2Done ? 'textSecondary' : 'error'}>
                    {!step1Done
                      ? 'Disponível depois do passo 1'
                      : (step2Done ? 'Número conectado' : 'Número ainda não conectado')}
                  </Typography>
                }
              >
                Conectar o WhatsApp
              </StepButton>
              <StepContent>{renderStep2()}</StepContent>
            </Step>

            <Step completed={step3Done} disabled={!step2Done}>
              <StepButton
                onClick={() => handleStepClick(2)}
                disabled={!step2Done}
                optional={
                  <Typography variant="caption" color="textSecondary">
                    {!step2Done
                      ? 'Disponível depois do passo 2'
                      : (useOwnNumber ? 'Seu número está ativo' : 'Seu número está desligado')}
                  </Typography>
                }
              >
                Ativar
              </StepButton>
              <StepContent>{renderStep3()}</StepContent>
            </Step>
          </Stepper>
        </>
      )}
    </Paper>
  );

  const whatsappIcon = <WhatsAppIcon />;

  return (
    <div className="page-content">
      <div className={styles.mainContainer}>
        {loading ? (
          <Paper
            elevation={0}
            sx={{
              ...cardSx,
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <div className={styles.loadingContainer}>
              <Loading size={40} />
              <Typography variant="body1" sx={{ color: ColorPalette.textMedium }}>
                Carregando configuração...
              </Typography>
            </div>
          </Paper>
        ) : (
          <>
            {!zapiAllowed && (
              <FeatureLockBanner
                type="permission"
                featureName="O número próprio de WhatsApp"
                planName="Profissional"
                onUpgradeClick={() => setUpgradeModalOpen(true)}
              />
            )}
            <PlanUpgradeModal
              isOpen={upgradeModalOpen}
              onClose={() => setUpgradeModalOpen(false)}
              featureName="O número próprio de WhatsApp"
              type="permission"
              planName="Profissional"
            />

            <div className={styles.pageHeader}>
              <AreaTitle
                Title="WhatsApp"
                Icon={whatsappIcon}
                Description="Conecte o seu número para as mensagens saírem sozinhas pelo sistema"
              />
            </div>

            <ExternalIntegrationInfo
              title="Número próprio via Z-API"
              description={[
                'Para usar um número próprio, a conta de WhatsApp é contratada na Z-API, nossa parceira.',
                'Sem número próprio, as mensagens saem pelo WhatsApp Web, com o texto pronto e um clique por envio.'
              ]}
              providerName="Z-API"
              providerUrl={ZAPI_SITE_URL}
              warning="O custo da Z-API é separado da assinatura da Gabriela."
            />

            <SenderOriginCard
              loading={senderLoading}
              state={senderState}
              own={senderOwn}
              platform={senderPlatform}
              phoneNumber={devicePhone}
              ownConnected={step2Done}
              saving={savingUseOwnNumber}
              onUsePlatformNumber={() => handleToggleUseOwnNumber(false)}
              onActivateOwnNumber={() => handleToggleUseOwnNumber(true)}
              onManageInstance={handleManageInstance}
              onReconnect={handleReconnect}
              ownNumberAllowed={zapiAllowed}
            />

            <OriginOverviewCard ownNumberAllowed={zapiAllowed} />

            {!setupExpanded && actionMessage && (
              <Alert
                severity={actionMessage.severity}
                sx={{ mb: 3 }}
                onClose={() => setActionMessage(null)}
              >
                {actionMessage.text}
              </Alert>
            )}

            {renderSetupCard()}
          </>
        )}
      </div>
    </div>
  );
}

export default Whatsapp;
